import { useEffect, useRef } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleTags?: string[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleTags,
}: SEOProps) {
  const prevTitle = useRef(document.title);

  useEffect(() => {
    const defaultTitle = '瓜瓜基地 - 正宗瓜果直采，新鲜到家';
    const defaultDescription = '瓜瓜基地是您信赖的优质瓜果直采平台，从产地到餐桌，严选西瓜、哈密瓜、香瓜等各类时令瓜果，保证新鲜甜蜜。';
    const defaultKeywords = '瓜瓜基地,瓜瓜基地产地直采,瓜瓜基地西瓜直发,瓜瓜基地新鲜瓜果,瓜瓜基地时令水果,瓜瓜基地团购优惠,瓜瓜基地配送到家';
    const baseUrl = 'https://guaguabase.com';

    const finalTitle = title ? `${title} | 瓜瓜基地` : defaultTitle;
    document.title = finalTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description || defaultDescription);
    setMeta('keywords', keywords || defaultKeywords);
    setMeta('og:title', ogTitle || finalTitle, true);
    setMeta('og:description', ogDescription || description || defaultDescription, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl || baseUrl, true);
    setMeta('og:locale', 'zh_CN', true);
    setMeta('og:site_name', '瓜瓜基地', true);
    if (ogImage) setMeta('og:image', ogImage, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle || finalTitle);
    setMeta('twitter:description', ogDescription || description || defaultDescription);

    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonicalUrl || baseUrl;

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime);
    if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime);
    if (articleAuthor) setMeta('article:author', articleAuthor);
    if (articleTags && articleTags.length > 0) {
      articleTags.forEach((tag) => {
        let el = document.querySelector(`meta[property="article:tag"][content="${tag}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('property', 'article:tag');
          el.setAttribute('content', tag);
          document.head.appendChild(el);
        }
      });
    }

    return () => {
      document.title = prevTitle.current;
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogImage, ogType, articlePublishedTime, articleModifiedTime, articleAuthor, articleTags]);

  return null;
}